/**
 * Shared upload intake for every document uploader.
 *
 * Both dropzones (report analyzer and Title Policy Analyzer) ran the same
 * sequence by hand: inspect the file (type, size, structure, real page count),
 * read the payload, then build a cost estimate for the preflight card. That
 * duplication is what let the two uploaders drift apart. This module is the
 * single implementation; the components only render its result.
 */

import { estimateUploadCost, type UploadCostEstimate } from "./upload-cost";
import {
  inspectUploadDocument,
  MAX_UPLOAD_TEXT_CHARS,
  type UploadInspection,
} from "./upload-limits";
import type { UploadSettings } from "./upload-settings";

export type UploadIntake =
  /** Failed a hard guardrail — show the rejection notice. */
  | { status: "rejected"; inspection: UploadInspection }
  /** Readable-but-unusable file — show a plain error line. */
  | { status: "error"; message: string }
  | { status: "pdf"; inspection: UploadInspection; fileDataUrl?: string; estimate: UploadCostEstimate }
  | { status: "text"; inspection: UploadInspection; text: string; estimate: UploadCostEstimate };

export function isPdfFile(file: File): boolean {
  return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("That file could not be read."));
    reader.readAsDataURL(file);
  });
}

/** Estimate the cost of a pasted Schedule B block. */
export function estimatePastedText(text: string): UploadCostEstimate {
  return estimateUploadCost({ textChars: text.length });
}

export async function prepareUpload(
  file: File,
  options: {
    settings: UploadSettings;
    /** Accept .txt Schedule B exports alongside PDFs. */
    allowText?: boolean;
    /** Read the PDF into a base64 data URL (needed by the title analyzer). */
    readPdfDataUrl?: boolean;
  },
): Promise<UploadIntake> {
  const { settings, allowText = false, readPdfDataUrl = false } = options;

  // Type, size, structure, then real page count — a 1,000-page scan, an
  // encrypted file, or a nesting bomb all stop here.
  const inspection = await inspectUploadDocument(file, {
    allowText,
    maxPages: settings.maxPages,
    pagesPerBlock: settings.pagesPerBlock,
  });
  if (!inspection.ok) return { status: "rejected", inspection };

  try {
    if (isPdfFile(file)) {
      const estimate = estimateUploadCost({ pages: inspection.pages });
      const fileDataUrl = readPdfDataUrl ? await readAsDataUrl(file) : undefined;
      return {
        status: "pdf",
        inspection,
        estimate,
        ...(fileDataUrl ? { fileDataUrl } : {}),
      };
    }

    const text = await file.text();
    if (text.length > MAX_UPLOAD_TEXT_CHARS) {
      return {
        status: "error",
        message: `That text file is too long. Paste or upload up to ${MAX_UPLOAD_TEXT_CHARS.toLocaleString()} characters of Schedule B text.`,
      };
    }
    if (text.trim().length < 40) {
      return {
        status: "error",
        message: "That text file looks empty. It has no Schedule B text to analyze.",
      };
    }
    return { status: "text", inspection, text, estimate: estimatePastedText(text) };
  } catch (err) {
    return {
      status: "error",
      message: err instanceof Error ? err.message : "That file could not be read.",
    };
  }
}
